import { ExpandingTooltip } from "../ExpandingTooltip";

export {
  TemplateType,
  TemplateTypeDescriptionDetailed,
  TemplateEnabled,
  DocumentType,
  InheritTransformRules,
};

// Item 3
const TemplateType: ExpandingTooltip = (short) => {
  if (short) return <p>Select the template type that you are configuring.</p>;
  return (
    <>
      <p>You can configure three types of templates:</p>
      {TemplateTypeDescriptionDetailed()}
    </>
  );
};

// Item 4
const TemplateEnabled: ExpandingTooltip = (short) => {
  if (short) return <p>Click to enable/disable this template.</p>;
  return (
    <p>
      Select the <strong>Template Enabled</strong> checkbox to enable the
      template to be used to classify documents. Uncheck the checkbox if you
      want to temporarily disable the template – in which case the template will
      not be used for classification and extraction. All new templates are
      enabled by default.
    </p>
  );
};

// Item 5
const DocumentType: ExpandingTooltip = (short) => {
  if (short)
    return <p>Select the Document Type that this template relates to.</p>;
  return (
    <p>
      Select the Document Type that this template relates to from the{" "}
      <strong>Document Type</strong> dropdown list. All Document Types that have
      been configured in Transact for this batch class are available for
      selection.
    </p>
  );
};

const InheritTransformRules: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>Click to enable/disable this inheritance of transformation rules.</p>
    );
  return (
    <p>
      Select the <strong>Inherit Transform Rules</strong> checkbox to enable
      transformation rule inheritance. When this checkbox is selected,
      template-level transformations will be applied first, followed by
      document-type level transformations if they exist. Without this checkbox,
      document-type level transformations are not run if there are
      template-level transformations that override them.
    </p>
  );
};

// common text
const TemplateTypeDescriptionDetailed: ExpandingTooltip = () => (
  <>
    <p>
      <strong>Specific Template</strong> (the default option)
    </p>
    <ul>
      <li>
        Used to classify a document as a specific supplier’s/customer's document
        for a specific document type (e.g., ABC Company’s invoice, or XYZ
        company’s purchase order).{" "}
      </li>
      <li>
        Provides the best extraction accuracy as 1:1 rules are built to extract
        the data.
      </li>
      <li>
        Uses Start Patterns that are specific to the supplier/customer document
        where all the start rules only appear on the first page of this
        supplier/customer document for this document type. For example, for an
        invoice:
        <ul>
          <li>
            Company name of supplier/customer (iI.e., something specific to the
            supplier/customer){" "}
          </li>
          <li>Tax Invoice (iI.e., something specific to the document type) </li>
          <li>Page 1 (i.e., something specific to the first page) </li>
        </ul>
      </li>
      <li>
        If a supplier has multiple formats, create multiple Specific templates
        with a <strong>consistent naming convention</strong>. For example:
        <ul>
          <li>{"<Supplier Name> <Supplier ID>"}</li>
          <li>{"<Supplier Name> <Supplier ID> 1"}</li>
          <li>{"<Supplier Name> <Supplier ID> 2"}</li>
        </ul>
      </li>
    </ul>
    <p>
      <strong>General Classification template</strong>
    </p>
    <ul>
      <li>
        Used to classify a document, that <u>doesn’t</u> have a Specific
        template, as a specific Document Type (e.g., Invoice, Credit Note, Sales
        Order).{" "}
      </li>
      <li>
        Uses Start Patterns that are common to the document type where all the
        start rules only appear on the first page of the document type. For
        example, for an invoice:
        <ul>
          <li>{"<Company Name>"}</li>
          <li>Tax Invoice </li>
          <li>Page 1\s</li>
        </ul>
      </li>
      <li>No extraction rules are configured. </li>
      <li>
        Documents that classify as a General Classification template will
        intelligently reuse extraction rules from Specific templates for the
        defined Document Type.
      </li>
      <li>
        Name the templates as something that relates to the Document Type (e.g.,
        General Invoice 1, General Invoice 2, General Sales Order).{" "}
      </li>
    </ul>
    <p>
      <strong>General Format template </strong>
    </p>
    <ul>
      <li>
        Used to classify documents that conform to a common <u>format</u> across
        suppliers.{" "}
      </li>
      <li>
        Often the format will be the default format for a system that many
        suppliers/customers will use (e.g., invoices produced by systems such as
        MYOB or Xero).{" "}
      </li>
      <li>
        Configure both Start Patterns and extraction rules so that the
        extraction is specific to the format (rather than using reuse from
        Specific templates).{" "}
      </li>
      <li>
        If all extraction rules fire, the document will not stop for review in
        Transact.{" "}
      </li>
      <li>
        If an extraction rule doesn’t fire, the system will automatically use
        reuse from Specific templates but will stop the document in Transact
        Validation for the field(s) to be reviewed.{" "}
      </li>
      <li>
        Very efficient option to extract data from multiple suppliers’ documents
        without needing to configure Specific templates for these
        suppliers.{" "}
      </li>
    </ul>
  </>
);
